import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { motion } from 'framer-motion';

const AccountSelection = ({ open, accounts = [], onSelect, onClose, loading }) => {
    const getAccountId = (account) => {
        if (account.student?.student_id) return account.student.student_id;
        if (account.teacher?.teacher_id) {
            const id = account.teacher.teacher_id;
            return id.startsWith('TID-') ? id : `TID-${id}`;
        }
        return null;
    };

    return (
        <Dialog
            open={open}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason === 'backdropClick') return;
                if (!loading && onClose) onClose();
            }}
            maxWidth="sm"
            fullWidth
            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: 'blur(8px)',
                        backgroundColor: 'rgba(15, 23, 42, 0.3)'
                    }
                }
            }}
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '36px !important',
                    padding: 0,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    overflow: 'hidden',
                    background: '#ffffff',
                    maxWidth: '480px',
                    width: '100%'
                }
            }}
        >
            <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 15, scale: 0.98 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                style={{ padding: '28px' }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        p: 0,
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Beautiful Animated Icon Container */}
                        <Box
                            sx={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '16px',
                                background: 'linear-gradient(135deg, rgba(255, 80, 45, 0.15) 0%, rgba(255, 120, 73, 0.05) 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                border: '1px solid rgba(255, 80, 45, 0.2)',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: -4,
                                    borderRadius: '20px',
                                    border: '1px solid rgba(255, 80, 45, 0.1)',
                                    animation: 'pulse 2s infinite'
                                },
                                '@keyframes pulse': {
                                    '0%': { transform: 'scale(1)', opacity: 1 },
                                    '50%': { transform: 'scale(1.08)', opacity: 0.5 },
                                    '100%': { transform: 'scale(1)', opacity: 1 }
                                }
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ff502d" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                        </Box>
                        
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 800,
                                color: '#0f172a',
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: '20px',
                                letterSpacing: '-0.5px'
                            }}
                        >
                            Select Account
                        </Typography>
                    </Box>

                    {!loading && onClose && (
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: '#94a3b8',
                                backgroundColor: '#f8fafc',
                                borderRadius: '12px',
                                p: 1,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    color: '#475569',
                                    backgroundColor: '#f1f5f9',
                                    transform: 'rotate(90deg)'
                                }
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </IconButton>
                    )}
                </DialogTitle>

                <DialogContent sx={{ p: 0, overflow: 'visible' }}>
                    <Typography
                        sx={{
                            fontSize: '14px',
                            color: '#64748b',
                            mb: 3,
                            lineHeight: 1.6,
                            fontFamily: "'Poppins', sans-serif"
                        }}
                    >
                        Multiple accounts are linked to this identifier. Select the account you want to reset password for.
                    </Typography>

                    {/* Staggered container */}
                    <Box
                        component={motion.div}
                        initial="hidden"
                        animate="show"
                        variants={{
                            hidden: { opacity: 0 },
                            show: {
                                opacity: 1,
                                transition: { staggerChildren: 0.08 }
                            }
                        }}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.8,
                            maxHeight: '330px',
                            overflowY: 'auto',
                            p: '8px',
                            m: '-8px',
                            pr: '12px',
                            mb: 3.5,
                            '&::-webkit-scrollbar': { width: '6px' },
                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                            '&::-webkit-scrollbar-thumb': { background: '#e2e8f0', borderRadius: '10px' },
                            '&::-webkit-scrollbar-thumb:hover': { background: '#cbd5e1' }
                        }}
                    >
                        {accounts.map((account, index) => {
                            const accountId = getAccountId(account);
                            const role = account.role ? account.role.toLowerCase() : 'user';
                            
                            // Role style customization
                            let roleColor = '#ff502d';
                            let roleBg = 'rgba(255, 80, 45, 0.08)';
                            let roleBorder = 'rgba(255, 80, 45, 0.15)';

                            if (role === 'teacher') {
                                roleColor = '#0284c7';
                                roleBg = 'rgba(2, 132, 199, 0.08)';
                                roleBorder = 'rgba(2, 132, 199, 0.15)';
                            } else if (role !== 'student') {
                                roleColor = '#475569';
                                roleBg = 'rgba(71, 85, 105, 0.08)';
                                roleBorder = 'rgba(71, 85, 105, 0.15)';
                            }

                            return (
                                <Box
                                    key={account.userId || account.id || index}
                                    component={motion.div}
                                    variants={{
                                        hidden: { opacity: 0, y: 15 },
                                        show: { opacity: 1, y: 0 }
                                    }}
                                    whileHover={loading ? {} : { y: -2, scale: 1.01 }}
                                    onClick={() => !loading && onSelect(account)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2.2,
                                        p: 2,
                                        border: '1.5px solid #e2e8f0',
                                        borderRadius: '24px',
                                        backgroundColor: '#ffffff',
                                        cursor: loading ? 'default' : 'pointer',
                                        transition: 'border-color 0.25s, box-shadow 0.25s, background-color 0.25s',
                                        opacity: loading ? 0.6 : 1,
                                        position: 'relative',
                                        '&:hover': {
                                            borderColor: loading ? '#e2e8f0' : '#ff502d',
                                            backgroundColor: loading ? '#ffffff' : '#fffcfb',
                                            boxShadow: loading ? 'none' : '0 10px 25px rgba(255, 80, 45, 0.08)',
                                            '& .selector-dot': {
                                                borderColor: '#ff502d',
                                                '&::after': {
                                                    transform: 'scale(1)'
                                                }
                                            }
                                        }
                                    }}
                                >
                                    {/* Avatar */}
                                    <Box
                                        sx={{
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '14px',
                                            background: 'linear-gradient(135deg, #ff502d 0%, #ff7849 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '2px solid #fff',
                                            boxShadow: '0 4px 12px rgba(255, 80, 45, 0.15)',
                                            flexShrink: 0,
                                            color: '#fff',
                                            fontWeight: 700,
                                            fontSize: '16px'
                                        }}
                                    >
                                        {account.profile_image ? (
                                            <Box
                                                component="img"
                                                src={account.profile_image}
                                                alt=""
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: '12px',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        ) : (
                                            (account.name || account.full_name || 'U').charAt(0).toUpperCase()
                                        )}
                                    </Box>

                                    {/* Info */}
                                    <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '15px',
                                                    color: '#0f172a',
                                                    fontFamily: "'Poppins', sans-serif",
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {account.name || account.full_name}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    fontSize: '10px',
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: '6px',
                                                    background: roleBg,
                                                    color: roleColor,
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    border: `1.5px solid ${roleBorder}`,
                                                    fontFamily: "'Poppins', sans-serif"
                                                }}
                                            >
                                                {role}
                                            </Box>
                                        </Box>

                                        <Typography
                                            sx={{
                                                fontSize: '12px',
                                                color: '#64748b',
                                                fontFamily: "'Poppins', sans-serif",
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {account.email || account.phone_number || account.phone}
                                        </Typography>

                                        {accountId && (
                                            <Box sx={{ display: 'flex', mt: 0.2 }}>
                                                <Box
                                                    sx={{
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        color: '#ff502d',
                                                        bgcolor: 'rgba(255, 80, 45, 0.05)',
                                                        px: 1,
                                                        py: 0.25,
                                                        borderRadius: '6px',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        border: '1px solid rgba(255, 80, 45, 0.08)',
                                                        fontFamily: "'Poppins', sans-serif"
                                                    }}
                                                >
                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
                                                        <line x1="7" y1="8" x2="17" y2="8" />
                                                        <line x1="7" y1="12" x2="17" y2="12" />
                                                        <line x1="7" y1="16" x2="13" y2="16" />
                                                    </svg>
                                                    {accountId}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Action Selector Indicator */}
                                    <Box
                                        className="selector-dot"
                                        sx={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            border: '2px solid #cbd5e1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                            transition: 'all 0.2s ease',
                                            position: 'relative',
                                            '&::after': {
                                                content: '""',
                                                width: '10px',
                                                height: '10px',
                                                borderRadius: '50%',
                                                backgroundColor: '#ff502d',
                                                transform: 'scale(0)',
                                                transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)'
                                            }
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </Box>

                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={loading}
                        sx={{
                            width: '100%',
                            borderColor: '#cbd5e1',
                            color: '#64748b',
                            borderRadius: '16px',
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '15px',
                            fontFamily: "'Poppins', sans-serif",
                            transition: 'all 0.2s',
                            '&:hover': {
                                borderColor: '#ff502d',
                                color: '#ff502d',
                                backgroundColor: 'rgba(255, 80, 45, 0.02)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </DialogContent>
            </motion.div>
        </Dialog>
    );
};

export default AccountSelection;
